import { useDeferredValue, useEffect, useMemo, useState, type ComponentType } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowRight,
  Bird,
  BookOpen,
  Bug,
  ChefHat,
  Fish,
  Flower2,
  MapPin,
  Newspaper,
  Package,
  PawPrint,
  Pickaxe,
  Search,
  Trophy,
  Users,
  Wheat,
} from "lucide-react";
import { useLocale } from "@/lib/i18n/LocaleContext";
import { localizePath } from "@/lib/i18n/localize-path";
import { getDiscoverResults, searchAll, type GlobalSearchResult, type SearchResultType } from "@/lib/globalSearch";

const KIND_ICONS: Record<string, ComponentType<{ className?: string }>> = {
  fish: Fish,
  insect: Bug,
  bird: Bird,
  crop: Wheat,
  flower: Flower2,
  forageable: Pickaxe,
  animal: PawPrint,
  dish: ChefHat,
};

const TYPE_ICONS: Record<SearchResultType, ComponentType<{ className?: string }>> = {
  item: Package,
  npc: Users,
  location: MapPin,
  guide: BookOpen,
  news: Newspaper,
  achievement: Trophy,
};

function resultIcon(result: GlobalSearchResult): ComponentType<{ className?: string }> {
  if (result.type === "item" && result.kind && KIND_ICONS[result.kind]) return KIND_ICONS[result.kind];
  return TYPE_ICONS[result.type];
}

interface DisplayResult {
  id: string;
  title: string;
  subtitle?: string;
  href: string;
  isFallback?: boolean;
  icon: ComponentType<{ className?: string }>;
  categoryLabel: string;
}

export function SearchModal({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t, locale } = useLocale();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  // The input itself stays bound to `query` (immediate) so typing never feels laggy; the actual
  // search — a filter pass over every item/NPC/location/guide/news/achievement, see
  // lib/globalSearch.ts — runs against this deferred value instead, so React can keep the
  // keystroke's own paint (the character appearing) off the critical path of that heavier work.
  const deferredQuery = useDeferredValue(query);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onOpenChange]);

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  useEffect(() => setSelectedIndex(0), [query]);

  const grouped = useMemo(() => {
    const results = deferredQuery.trim() ? searchAll(deferredQuery, locale) : getDiscoverResults(locale);
    const categoryLabels: Record<SearchResultType, string> = {
      item: t("search.categoryItems"),
      npc: t("search.categoryNpcs"),
      location: t("search.categoryLocations"),
      guide: t("search.categoryGuides"),
      news: t("search.categoryNews"),
      achievement: t("search.categoryAchievements"),
    };

    const byType = new Map<SearchResultType, DisplayResult[]>();
    for (const result of results) {
      const list = byType.get(result.type) ?? [];
      list.push({
        id: result.id,
        title: result.title,
        subtitle: result.subtitle,
        href: result.href,
        isFallback: result.isFallback,
        icon: resultIcon(result),
        categoryLabel: categoryLabels[result.type],
      });
      byType.set(result.type, list);
    }

    return Array.from(byType.entries()).map(([type, items]) => ({ label: categoryLabels[type], items, type }));
  }, [deferredQuery, locale, t]);

  const flatResults = useMemo(() => grouped.flatMap((group) => group.items), [grouped]);

  function handleSelect(href: string) {
    onOpenChange(false);
    navigate(localizePath(href, locale));
  }

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, flatResults.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && flatResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(flatResults[selectedIndex].href);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedIndex, flatResults]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="top-[12%] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-[2rem] border-4 border-white bg-white p-0 shadow-2xl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t("search.placeholder")}</DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-3 border-b border-border/60 px-6 py-5">
          <Search className="size-5 shrink-0 text-primary" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("search.placeholder")}
            className="h-auto flex-1 border-0 bg-transparent p-0 text-base font-semibold placeholder:text-muted-foreground focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded-lg border border-border bg-muted px-2 py-1 font-mono text-xs text-muted-foreground sm:inline-flex">
            Esc
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-3 py-3">
          {deferredQuery.trim() && flatResults.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-14 text-center text-muted-foreground">
              <Search className="mb-3 size-9 opacity-30" />
              <p className="font-bold">{t("search.noResults")}</p>
              <p className="mt-1 text-sm">{t("search.noResultsHint")}</p>
            </div>
          ) : (
            grouped.map((group) => (
              <div key={group.label} className="mb-2">
                <div className="px-3 py-2 text-[11px] font-black uppercase tracking-[0.2em] text-primary">
                  {group.label}
                </div>
                {group.items.map((item) => {
                  const globalIndex = flatResults.indexOf(item);
                  const isSelected = globalIndex === selectedIndex;
                  const Icon = item.icon;
                  return (
                    <button
                      key={`${group.label}:${item.id}`}
                      type="button"
                      onClick={() => handleSelect(item.href)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                      className={`group flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-all ${
                        isSelected
                          ? "border-2 border-primary/20 bg-primary/10"
                          : "border-2 border-transparent hover:bg-secondary/50"
                      }`}
                    >
                      <div
                        className={`flex size-9 shrink-0 items-center justify-center rounded-xl bg-secondary/50 text-muted-foreground transition-transform ${
                          isSelected ? "scale-110 text-primary" : "group-hover:scale-105"
                        }`}
                      >
                        <Icon className="size-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-bold">{item.title}</div>
                        {item.subtitle ? (
                          <div className="truncate text-xs text-muted-foreground">{item.subtitle}</div>
                        ) : null}
                      </div>
                      {isSelected ? <ArrowRight className="size-4 shrink-0 text-primary" /> : null}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="hidden items-center gap-3 border-t border-border/60 bg-secondary/20 px-6 py-3 text-xs text-muted-foreground sm:flex">
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-mono">↑↓</kbd>
            {t("search.navigate")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-mono">↵</kbd>
            {t("search.select")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <kbd className="rounded border border-border bg-background px-1.5 py-0.5 font-mono">esc</kbd>
            {t("search.close")}
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
